'use client'

import { useState } from 'react'
import { MdHowToReg, MdRefresh, MdTune } from 'react-icons/md'
import { ListPageHeader } from '@/components/list-page'
import PresenceAssign from '@/components/presence/presence-assign'
import PresenceHistory from '@/components/presence/presence-history'
import PresenceList from '@/components/presence/presence-list'
import PresenceStatusManager from '@/components/presence/presence-status-manager'
import { usePresence } from '@/lib/hooks/use-presence'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'

type PresenceView = 'PANEL' | 'ASSIGN' | 'HISTORY' | 'STATUSES'

/**
 * Tela exclusiva do controle de presença — pensada para ficar aberta num monitor
 * da recepção ou na sala do profissional: a lista se atualiza sozinha por socket,
 * sem ninguém precisar recarregar a página.
 */
export default function PresencePage() {
    const { canCreate, canUpdate } = useResourcePermissions('bookings', 'presence')
    const [view, setView] = useState<PresenceView>('PANEL')
    const { entries, statuses, loading, reload, loadPanel, loadStatuses } =
        usePresence()

    return (
        <div className="flex flex-col gap-4 p-4">
            <ListPageHeader
                icon={<MdHowToReg className="text-2xl" />}
                title="Controle de presença"
                subtitle={
                    loading
                        ? 'Carregando...'
                        : `${entries.length} paciente(s) no balcão agora`
                }
                actions={
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-2 !rounded-lg"
                        onClick={reload}
                    >
                        <MdRefresh className="text-lg" />
                        <span className="hidden sm:inline">Atualizar</span>
                    </button>
                }
            />

            <div role="tablist" className="tabs tabs-bordered">
                <button
                    type="button"
                    role="tab"
                    className={`tab ${view === 'PANEL' ? 'tab-active' : ''}`}
                    onClick={() => setView('PANEL')}
                >
                    Painel ({entries.length})
                </button>
                {canCreate && (
                    <button
                        type="button"
                        role="tab"
                        className={`tab ${view === 'ASSIGN' ? 'tab-active' : ''}`}
                        onClick={() => setView('ASSIGN')}
                    >
                        Definir controle
                    </button>
                )}
                <button
                    type="button"
                    role="tab"
                    className={`tab ${view === 'HISTORY' ? 'tab-active' : ''}`}
                    onClick={() => setView('HISTORY')}
                >
                    Histórico
                </button>
                {canUpdate && (
                    <button
                        type="button"
                        role="tab"
                        className={`tab gap-2 ${view === 'STATUSES' ? 'tab-active' : ''}`}
                        onClick={() => setView('STATUSES')}
                    >
                        <MdTune className="text-base" />
                        Controles
                    </button>
                )}
            </div>

            <div className="rounded-2xl border border-base-200 bg-base-100 p-4">
                {view === 'PANEL' && (
                    <PresenceList
                        entries={entries}
                        statuses={statuses}
                        loading={loading}
                        readOnly={!canUpdate}
                        onChanged={loadPanel}
                        dense
                    />
                )}
                {view === 'ASSIGN' && (
                    <PresenceAssign statuses={statuses} onAssigned={loadPanel} />
                )}
                {view === 'HISTORY' && <PresenceHistory />}
                {view === 'STATUSES' && (
                    <PresenceStatusManager
                        statuses={statuses}
                        onChanged={loadStatuses}
                    />
                )}
            </div>
        </div>
    )
}
